import type { Answers, QolImpact, RootCause, TSuppressionProfile } from "./_schema";
import { deriveBoostProfile, type ExtraSignals } from "./boost-profile";

/**
 * Libido / "manopause" quiz deriver. Wraps `deriveBoostProfile` (the
 * cortisol-quiz pattern) with five additions:
 *
 *   1. Answer translation: `when-started` is an ignite-verbatim passthrough;
 *      `age`, `energy-day` and `body-change` are translated onto the quiz-v6
 *      answer space so they feed severity AND root causes ORGANICALLY.
 *   2. Sign severity: the six libido-specific sign questions feed severity via
 *      the `extraSignals` seam (worst answer -> tier1, next -> tier2/tier3, the
 *      quiz-v7 cohort-gradient convention).
 *   3. Forced primary: `blocked-testosterone` leads the verdict on every path.
 *   4. QoL floor: the `confidence` screen is a direct self-report of functional
 *      cost ("inside and outside of the bedroom"), which `deriveQolImpact`
 *      cannot see from this quiz's answers -- without the floor, `high` is
 *      unreachable on every path (see the reachability note below).
 *   5. On-angle secondary guard: the base deriver's generic backfill causes are
 *      swapped for `age-related-decline` so the 2x2 grid always reads on-angle.
 *
 * WHY `blocked-testosterone` IS THE FORCED PRIMARY (operator decision,
 * 2026-08-21). The angle's clinical spine is the EMAS "late-onset hypogonadism"
 * triad -- low libido, weak erections, poor morning erections -- which is
 * exactly what this quiz's Q1 / Q4 / Q2 ask (docs/t-suppression-research.md
 * section 1). The mechanism behind that triad in an aging man is the free-T
 * handcuff: research doc section 3I records that SHBG rises ~1% per year,
 * "reducing free T even when total T is stable". `blocked-testosterone` names
 * that ("your testosterone isn't missing -- it's being locked away"), and it
 * does so WITHOUT blaming age, which matters twice over:
 *
 *   - `age-related-decline` was the other candidate. Its copy pays off the
 *     bridge's closing line nearly verbatim, but section 3I bounds it to "age
 *     50+", and this quiz offers `under-35` and `35-39` age buckets. Forcing it
 *     would tell a 30-year-old his body "has begun the natural age-related
 *     decline" -- a claim the governing research doc does not support.
 *   - The label itself has to survive being read straight after a bridge that
 *     says these signs point to "more than 'just getting older'". A verdict
 *     card reading AGE-RELATED DECLINE contradicts that sentence; BLOCKED
 *     TESTOSTERONE does not, and it carries a reversible register ("locked
 *     away" implies a key) that age cannot.
 *
 * Known gap, accepted by the operator: neither bridge names SHBG or binding, so
 * the verdict introduces its mechanism one screen after the flow stops talking.
 * `age-related-decline` remains the organic Secondary on the paths where it
 * scores, which keeps the aging thread visible in the grid. If the bridges are
 * ever rewritten to seed the "bound, not missing" idea, nothing here changes.
 *
 * Second known cost, measured rather than assumed: `blocked-testosterone`'s
 * explanation is 160 characters against the ~110-char house median rule 11a
 * asks for, and the assessment card is fold-critical. Measured over CDP at
 * 375x667, result-page height and CONTINUE-button top:
 *
 *   quiz-v6 / cortisol      (no cohort, 110 ch)  769px, CONTINUE at 577
 *   quiz-before-trt        (no cohort, 160 ch)  788px, CONTINUE at 596
 *   quiz-sleep / quiz-beer (cohort,    ~110 ch) 838px, CONTINUE at 646
 *   quiz-libido            (cohort,    160 ch)  857px, CONTINUE at 665
 *
 * Exactly additive: a cohort costs +69px (its eyebrow plus the italic subline)
 * and the long explanation +19px. So this quiz is 19px past the live
 * cohort-quiz baseline, and CONTINUE sits below a 667 fold on all three cohort
 * quizzes -- a platform-level issue, not one this quiz introduces.
 *
 * The 19px is deliberately NOT bought back through the `copy` seam. Overriding
 * just this one entry for this one quiz would leave quiz-before-trt (live)
 * showing different prose for the same root cause, and two divergent texts for
 * one mechanism cost more in maintenance than 19px costs in scroll. If the
 * explanation is ever compressed toward the house median, do it in
 * EN_PROFILE_COPY so both quizzes benefit -- with operator sign-off, since it
 * changes live quiz-before-trt copy.
 *
 * On-angle guard: nothing is translated to `stress-symptoms`, `sleep-hours`,
 * `family-history` or `processed-foods`, so `cortisol-dominance`,
 * `chronic-stress`, `sleep-deprivation`, `metabolic-syndrome`, `overtraining`
 * and `endocrine-disruption` are all unscoreable. Only `visceral-adiposity`
 * (from `body-change`) and `age-related-decline` (from `age` + `when-started`)
 * can score organically, so the Secondary card is always one of those two --
 * both on-angle for a manopause verdict, and `visceral-adiposity` (rendered
 * AROMATASE LOAD) directly supports the forced primary's "too much is converted
 * to estrogen first" clause. Verified across the full 1,048,576-path space in
 * tests/quizzes/quiz-libido.test.ts.
 *
 * Pure function of `answers` -- reproducible server-side from the persisted
 * submission (rule 11).
 */

/** Ids whose values are ignite-verbatim -- copied through untranslated. */
const PASSTHROUGH_IDS = ["when-started"] as const;

/**
 * `age` -> quiz-v6 `age`. The quiz keeps the operator's own buckets (the ad copy
 * splits at 35, quiz-v6 splits at 30) so `quiz_events` records the label the user
 * actually saw; the two extra-fine buckets collapse onto quiz-v6's coarser ones.
 * `35-39` -> `30-39` and `under-35` -> `20-29` both land in quiz-v6's
 * "contributes no age risk" region, which is the correct read either way.
 */
const AGE_TO_IGNITE: Record<string, "50-plus" | "40-49" | "30-39" | "20-29"> = {
  "50-plus": "50-plus",
  "40-49": "40-49",
  "35-39": "30-39",
  "under-35": "20-29",
};

/**
 * `energy-day` -> quiz-v6 `energy-3pm`. Both are monotone 4-point severity
 * ladders of the same construct (daytime energy), so the mapping is positional:
 * worst-to-worst, best-to-best. Same idiom as quiz-sleep's WAKE_TO_HOURS.
 *
 * A distinct id is used rather than reusing `energy-3pm` verbatim because the
 * operator's middle rungs are not quiz-v6's: "I'm tired more often than I used
 * to be" is a whole-day complaint where quiz-v6's `afternoon-slump` is a 3pm
 * one. Keeping our own ids means the admin breakdown shows the label the user
 * read; the translation is what feeds the deriver.
 */
const ENERGY_TO_IGNITE: Record<
  string,
  "drained" | "afternoon-slump" | "slightly-tired" | "same-energy"
> = {
  "running-empty": "drained",
  "tired-often": "afternoon-slump",
  "afternoon-slump": "slightly-tired",
  plenty: "same-energy",
};

/**
 * `body-change` -> quiz-v6 `body-composition`. NOT a severity ladder -- the
 * operator's options are KINDS of change, so each is mapped to the quiz-v6
 * bucket that makes the same claim:
 *
 *   belly-chest-jaw -> overweight        (visible central + chest adiposity)
 *   less-muscle     -> soft-some-muscle  (lost mass, not gained fat)
 *   softer-overall  -> soft-some-muscle  (recomposition at stable weight)
 *   not-really      -> average           (see below)
 *
 * `not-really` deliberately maps to `average`, NOT `lean-muscular`: the user
 * said nothing changed, not that he is lean. `lean-muscular` is the gate on the
 * base deriver's "optimization profile" decline-pattern branch and on
 * `overtraining` scoring, and neither belongs in a flow that never asks about
 * training. This also means `lean-muscular` is unreachable from this quiz.
 */
const BODY_TO_IGNITE: Record<string, "average" | "soft-some-muscle" | "overweight"> = {
  "belly-chest-jaw": "overweight",
  "less-muscle": "soft-some-muscle",
  "softer-overall": "soft-some-muscle",
  "not-really": "average",
};

/**
 * Sign-question severity mapping. `age`, `energy-day`, `body-change` and
 * `when-started` are absent by design -- they already feed severity organically
 * through the translations above, and a signal must never be counted twice.
 *
 * tier1 holds four DISTINCT constructs, matching the research doc's tier-1 list
 * one for one: low libido (`libido`), erectile difficulty (`reliability`), loss
 * of morning erections (`morning-wood`) -- the EMAS triad whose CLUSTERING is
 * what defines late-onset hypogonadism -- plus the doc's "depressed mood / loss
 * of vigor" domain, which this angle reads as `confidence`. `confidence` is also
 * the QoL floor input below; feeding both is the same arrangement quiz-sleep has
 * for `sleep-cost`, and it is not a double count because severity and QoL are
 * separate outputs.
 *
 * `pill-reliance` sits at tier2 rather than tier1 because it measures the same
 * construct as `reliability` (erectile function) from the coping side --
 * promoting it would double-count erectile difficulty at the top tier.
 * `sex-thoughts` sits at tier3 for the same reason against `libido` (both
 * measure desire); it is the frequency companion to `libido`'s magnitude, the
 * same relationship quiz-sleep has between `morning-state` and `unrefreshed`.
 *
 * Measured over the full 1,048,576-path space, this produces EARLY DECLINE
 * 41.67% / DECLINING 35.32% / SUPPRESSED 23.00%. `confidence` was at tier2 in
 * the first draft, which put 49.49% of completers on the softest verdict --
 * half the traffic reading the mildest thing the deriver can say. Do not demote
 * it back without re-checking that split.
 */
const TIER1_SIGNS: Record<string, readonly string[]> = {
  libido: ["significantly-lower"],
  "morning-wood": ["cant-remember"],
  reliability: ["much-less-often"],
  confidence: ["definitely"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  libido: ["somewhat-lower"],
  "morning-wood": ["rarely"],
  reliability: ["hit-or-miss"],
  "pill-reliance": ["almost-every-time"],
};
const TIER3_SIGNS: Record<string, readonly string[]> = {
  "morning-wood": ["few-times-week"],
  reliability: ["usually"],
  "sex-thoughts": ["much-less"],
  "pill-reliance": ["pretty-often"],
  confidence: ["somewhat"],
};

/**
 * `confidence` -> QoL floor. It is the one screen that asks what the pattern
 * COSTS rather than how often it happens ("inside and outside of the bedroom"),
 * and its own why-we-ask names the spill into the rest of his life -- so it is
 * a direct functional-impact self-report, the same justification quiz-sleep has
 * for `sleep-cost`.
 *
 * Load-bearing, not decorative. `deriveQolImpact` reads `concern`, `energy-3pm`,
 * `stress-symptoms`, `body-composition` and `sleep-hours`. This quiz feeds only
 * two of those five, capping tier1 hits at 1 and the total at 2 -- which is
 * exactly `medium`. Without this floor, `high` QoL is unreachable on all
 * 1,048,576 paths, including the one where every symptom is at its worst.
 * The two milder answers set no floor and leave the derived value alone.
 */
const CONFIDENCE_TO_QOL_FLOOR: Record<string, QolImpact> = {
  definitely: "high",
  somewhat: "medium",
};

/**
 * Root causes that can only reach the grid through the base deriver's generic
 * ROOT_CAUSE_BACKFILL, never through this quiz's own answers -- so a card
 * showing one of them would be asserting something the user was never asked
 * about. Swapped for `age-related-decline` by `withOnAngleSecondary`.
 */
const OFF_ANGLE_BACKFILL: readonly RootCause[] = ["lifestyle-load", "endocrine-disruption"];

/** The on-angle stand-in. Also the organic Secondary wherever it does score. */
const ON_ANGLE_SECONDARY: RootCause = "age-related-decline";

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
    tier3: countTier(answers, TIER3_SIGNS),
  };
}

function qolFloorFor(answers: Answers): QolImpact | undefined {
  const confidence = answers["confidence"];
  if (typeof confidence !== "string") return undefined;
  return CONFIDENCE_TO_QOL_FLOOR[confidence];
}

/**
 * Builds the ignite-namespace answer set the base deriver reads: one passthrough
 * id plus the three translations. No fabricated symptom answers -- the
 * libido-specific sign severity comes from `extraSignals`.
 *
 * Deliberately does NOT synthesise `concern` or `results-want`. `concern ===
 * "sex-drive"` would add a tier1 point in `countSignals` and a tier1 QoL domain
 * on top of the direct `libido` screen, double-counting the same signal (the
 * mistake quiz-sleep's GOAL_TO_RESULTS comment calls out). `results-want` feeds
 * only `expectedOutcome`, which the result page has not rendered since the
 * verdict-first redesign (rule 13), so synthesising it would change nothing a
 * user or a downstream consumer can see.
 */
function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  const age = answers["age"];
  if (typeof age === "string" && age in AGE_TO_IGNITE) {
    out["age"] = AGE_TO_IGNITE[age]!;
  }
  const energy = answers["energy-day"];
  if (typeof energy === "string" && energy in ENERGY_TO_IGNITE) {
    out["energy-3pm"] = ENERGY_TO_IGNITE[energy]!;
  }
  const body = answers["body-change"];
  if (typeof body === "string" && body in BODY_TO_IGNITE) {
    out["body-composition"] = BODY_TO_IGNITE[body]!;
  }
  return out;
}

/**
 * Replaces an off-angle Secondary with `age-related-decline`.
 *
 * Why this is needed: with `blocked-testosterone` forced into slot 0, slot 1 is
 * the organic top scorer -- but on the paths where NOTHING scores organically
 * (`body-change: not-really` combined with an age/onset pair that scores no age
 * risk, ~17% of the space) the base deriver's ROOT_CAUSE_BACKFILL fills slot 1
 * with `lifestyle-load`, whose copy asserts "several lifestyle inputs" on a
 * quiz that asks about zero lifestyle inputs. `age-related-decline` is the
 * honest stand-in for a man reporting a multi-year drive slide with no body
 * change, it is the third entry in that same backfill list, and it keeps the
 * manopause thread in the grid on 100% of paths.
 *
 * Output-shaping only -- the base deriver is untouched, the same way quiz-v7
 * wraps `evidence`. Cannot collide with the forced primary, which is never
 * `age-related-decline`.
 */
function withOnAngleSecondary(rootCauses: readonly RootCause[]): RootCause[] {
  return rootCauses.map((cause, i) =>
    i > 0 && OFF_ANGLE_BACKFILL.includes(cause) ? ON_ANGLE_SECONDARY : cause,
  );
}

export function deriveLibidoProfile(answers: Answers): TSuppressionProfile {
  const profile = deriveBoostProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
    forcePrimaryCause: "blocked-testosterone",
    qolFloor: qolFloorFor(answers),
  });
  return {
    ...profile,
    rootCauses: withOnAngleSecondary(profile.rootCauses),
    // Drives the drive-specific eyebrow / subline / projection subhead / offer
    // headline via COHORT_OVERLAYS (quizzes/quiz-v7.ts). `sex-drive` already
    // exists there as a quiz-v7 concern, and its copy is on-angle verbatim
    // ("This is the upstream cause of the slip in drive you described."), so
    // this quiz needs no new cohort key.
    cohort: "sex-drive",
  };
}
